import { createContext, useCallback, useMemo, useRef, useSyncExternalStore, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { avatarPhoto } from '../../lib/avatar';
import type { Mentionable } from '../../lib/comment-types';
import type { AvatarPhoto } from '../../ui/Avatar';
import type { SceneStore } from '@os/doc';
import { authorIds, type AuthorsSource } from './authors';
import { useAuthorNames } from './useAuthors';

/** The first name a sticky shows for its author (P5e, FigJam-like), or null: unknown, former member, local board. */
export type AuthorLabel = (userId: string | undefined) => string | null;
const none: AuthorLabel = () => null;
export const CanvasAuthorsContext = createContext<AuthorLabel>(none);

/** Someone named on the canvas (a task's assignee or checker, P16-5); null while their name loads. */
export type PersonLookup = (userId: string | undefined) => { name: string; color: string | null; photo?: AvatarPhoto; you: boolean } | null;
/** People of a server board (null on a local board): who is who, and who can be assigned a task. */
export type CanvasPeople = { boardId: string; person: PersonLookup; people?: () => Promise<Mentionable[]> };
export const CanvasPeopleContext = createContext<CanvasPeople | null>(null);

const firstName = (name: string) => name.trim().split(/\s+/)[0] || null;
const cache = new WeakMap<SceneStore, { key: string; ids: string[] }>();

/**
 * Every author of the board (sorted, stable while it does not change), read on list changes and on document updates:
 * assigning or checking a task changes no list (P16-5).
 */
function useBoardAuthorIds(store: SceneStore): string[] {
  const subscribe = useCallback(
    (cb: () => void) => {
      const off = store.subscribeList(cb);
      store.doc.on('update', cb);
      return () => {
        off();
        store.doc.off('update', cb);
      };
    },
    [store],
  );
  return useSyncExternalStore(subscribe, () => {
    const ids = authorIds(store.list());
    const key = ids.join(',');
    const hit = cache.get(store);
    if (hit?.key === key) return hit.ids;
    cache.set(store, { key, ids });
    return ids;
  });
}

function Resolved({ store, source, children }: { store: SceneStore; source: AuthorsSource; children: ReactNode }) {
  const loaded = useAuthorNames(source, useBoardAuthorIds(store));
  // A new author's first sticky changes the request: keep the names already known meanwhile (no label flickers).
  const last = useRef<ReadonlyMap<string, Mentionable> | null>(null);
  if (loaded) last.current = loaded;
  const names = loaded ?? last.current;
  const label = useCallback<AuthorLabel>(
    (id) => {
      if (!id) return null;
      if (id === source.me) return source.meName ? firstName(source.meName) : null;
      const m = names?.get(id);
      return m ? firstName(m.displayName) : null;
    },
    [names, source.me, source.meName],
  );
  const { t } = useTranslation();
  const pending = loaded === null;
  const person = useCallback<PersonLookup>(
    (id) => {
      if (!id) return null;
      if (id === source.me) return { name: source.meName || t('authors.you'), color: null, photo: avatarPhoto(source.meAvatarKey, id), you: true };
      const m = names?.get(id);
      if (m) return { name: m.displayName || t('authors.former'), color: m.color, photo: avatarPhoto(m.avatarKey, id), you: false };
      return pending ? null : { name: t('authors.former'), color: null, you: false };
    },
    [names, pending, source.me, source.meName, source.meAvatarKey, t],
  );
  const people = useMemo<CanvasPeople>(() => ({ boardId: source.boardId, person, people: source.people }), [source.boardId, source.people, person]);
  return (
    <CanvasAuthorsContext.Provider value={label}>
      <CanvasPeopleContext.Provider value={people}>{children}</CanvasPeopleContext.Provider>
    </CanvasAuthorsContext.Provider>
  );
}

/** Resolves the names of the board's authors in one request per set of ids (server boards; local ones show none). */
export function CanvasAuthorsProvider({ store, source, children }: { store: SceneStore; source: AuthorsSource | null | undefined; children: ReactNode }) {
  return source ? (
    <Resolved store={store} source={source}>
      {children}
    </Resolved>
  ) : (
    children
  );
}
